import { useEffect, useRef, type PointerEvent as ReactPointerEvent } from "react";
import { GRID_SIZE } from "../../constants/editor";
import type { PixelGrid } from "../../shared/game";

type PixelEditorCanvasProps = {
  grid: PixelGrid;
  size: number;
  onPointerStart: (row: number, col: number) => void;
  onPointerMove: (row: number, col: number) => void;
  onPointerEnd: () => void;
};

export function PixelEditorCanvas({
  grid,
  size,
  onPointerStart,
  onPointerMove,
  onPointerEnd,
}: PixelEditorCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const draggingRef = useRef(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext("2d");
    if (!canvas || !context) {
      return;
    }

    const cellSize = size / GRID_SIZE;
    context.clearRect(0, 0, size, size);

    for (let row = 0; row < GRID_SIZE; row += 1) {
      for (let col = 0; col < GRID_SIZE; col += 1) {
        context.fillStyle = grid[row][col] ?? ((row + col) % 2 === 0 ? "#0d0b18" : "#120f1e");
        context.fillRect(col * cellSize, row * cellSize, cellSize, cellSize);
      }
    }

    context.strokeStyle = "rgba(255,255,255,0.04)";
    context.lineWidth = 1;
    for (let row = 0; row <= GRID_SIZE; row += 1) {
      context.beginPath();
      context.moveTo(0, row * cellSize);
      context.lineTo(size, row * cellSize);
      context.stroke();
    }
    for (let col = 0; col <= GRID_SIZE; col += 1) {
      context.beginPath();
      context.moveTo(col * cellSize, 0);
      context.lineTo(col * cellSize, size);
      context.stroke();
    }
  }, [grid, size]);

  const toCell = (event: ReactPointerEvent<HTMLCanvasElement>) => {
    const bounds = event.currentTarget.getBoundingClientRect();
    const col = Math.max(0, Math.min(GRID_SIZE - 1, Math.floor(((event.clientX - bounds.left) / bounds.width) * GRID_SIZE)));
    const row = Math.max(0, Math.min(GRID_SIZE - 1, Math.floor(((event.clientY - bounds.top) / bounds.height) * GRID_SIZE)));
    return { row, col };
  };

  return (
    <canvas
      ref={canvasRef}
      width={size}
      height={size}
      style={{ display: "block", width: size, height: size, imageRendering: "pixelated", cursor: "crosshair" }}
      onPointerDown={(event) => {
        draggingRef.current = true;
        event.currentTarget.setPointerCapture(event.pointerId);
        const { row, col } = toCell(event);
        onPointerStart(row, col);
      }}
      onPointerMove={(event) => {
        if (!draggingRef.current) {
          return;
        }

        const { row, col } = toCell(event);
        onPointerMove(row, col);
      }}
      onPointerUp={(event) => {
        draggingRef.current = false;
        event.currentTarget.releasePointerCapture(event.pointerId);
        onPointerEnd();
      }}
      onPointerLeave={() => {
        if (!draggingRef.current) {
          return;
        }

        draggingRef.current = false;
        onPointerEnd();
      }}
    />
  );
}
